// src/pages/TicketDeliveryManagement/modals/DeliveryDetailModal.tsx
// Modal xem chi tiết 1 phiếu giao vé (thông tin phiếu + các dòng dải số)

import React, { useEffect, useState } from 'react';
import { Button, Collapse, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { AgentMap, getAgentCode, getAgentName } from 'helpers/agentLookup';
import {
  TICKET_DELIVERY_STATUS_COLORS,
  TICKET_DELIVERY_STATUS_LABELS,
  TicketDelivery,
} from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_delivery_management');

interface DeliveryDetailModalProps {
  isOpen: boolean;
  delivery: TicketDelivery | null;
  agentMap: AgentMap;
  onPrint?: () => void;
  onClose: () => void;
}

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '-';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

// Phiếu nhiều dòng thì mặc định thu gọn để modal không quá dài
const COLLAPSE_THRESHOLD = 5;

const DeliveryDetailModal: React.FC<DeliveryDetailModalProps> = ({
  isOpen,
  delivery,
  agentMap,
  onPrint,
  onClose,
}) => {
  const location = useLocation();
  const lineCount = delivery?.lines?.length || 0;
  const [linesOpen, setLinesOpen] = useState(true);

  useEffect(() => {
    if (isOpen) setLinesOpen(lineCount <= COLLAPSE_THRESHOLD);
  }, [isOpen, lineCount]);

  if (!delivery) return null;

  const color =
    delivery.statusBadgeColor || TICKET_DELIVERY_STATUS_COLORS[String(delivery.status)] || 'secondary';
  const label =
    delivery.statusName || TICKET_DELIVERY_STATUS_LABELS[String(delivery.status)] || st('khong_ro_text');

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{st('chi_tiet_phieu_giao_ve_title')} {delivery.deliveryCode}</ModalHeader>
      <ModalBody>
        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td width="30%" className="fw-semibold bg-light">
                  {st('ma_phieu_column')}
                </td>
                <td>{delivery.deliveryCode}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('trang_thai_column')}</td>
                <td>
                  <span className={`badge bg-${color}-subtle text-${color} text-uppercase`}>
                    {label}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ke_hoach_phat_hanh_column')}</td>
                <td>{delivery.planCode || delivery.issuancePlanId}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('dai_ly_nhan_text')}</td>
                <td>
                  {getAgentName(agentMap, delivery.agentId)}{' '}
                  <small className="text-muted">({getAgentCode(agentMap, delivery.agentId)})</small>
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('tong_so_ve_text')}</td>
                <td className="fw-semibold">{formatNumber(delivery.totalQuantity)}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('gia_ve_ap_dung_text')}</td>
                <td>
                  {typeof delivery.unitPrice === 'number'
                    ? st('value_d_ve_text', { value: formatNumber(delivery.unitPrice) })
                    : '-'}
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('gia_tri_uoc_tinh_text')}</td>
                <td className="fw-semibold">{formatCurrency(delivery.estimatedValue)}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('nguoi_lap_column')}</td>
                <td>{delivery.deliveredByName || delivery.createdByName || '-'}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ngay_giao_column')}</td>
                <td>
                  {delivery.deliveredAt
                    ? moment(delivery.deliveredAt).format('DD/MM/YYYY HH:mm')
                    : '-'}
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ghi_chu_label')}</td>
                <td>{delivery.note || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Khối chi tiết dải số có thể thu gọn/mở rộng — icon mũi tên đổi chiều theo state:
            đang mở => ri-arrow-up-s-line, đang thu gọn => ri-arrow-down-s-line */}
        <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
          <h6 className="mb-0">{st('chi_tiet_dai_so_title')}{formatNumber(lineCount)} {st('dong_title')}</h6>
          <Button
            color="link"
            size="sm"
            className="p-0 text-decoration-none"
            type="button"
            aria-expanded={linesOpen}
            data-action-code="button-toggle-lines"
            data-action-name="Thu gọn / mở rộng chi tiết dải số"
            onClick={() => setLinesOpen((prev) => !prev)}
          >
            {linesOpen ? st('thu_gon_text') : st('mo_rong_text')}
            <i className={`${linesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} align-bottom ms-1`}></i>
          </Button>
        </div>

        <Collapse isOpen={linesOpen}>
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>{st('ky_hieu_column')}</th>
                  <th>{st('tu_so_column')}</th>
                  <th>{st('den_so_column')}</th>
                  <th className="text-end">{st('so_luong_column')}</th>
                </tr>
              </thead>
              <tbody>
                {lineCount === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">
                      {st('khong_co_dong_dai_so_text')}
                    </td>
                  </tr>
                ) : (
                  (delivery.lines || []).map((line, index) => (
                    <tr key={line.id ?? index}>
                      <td>{line.symbol}</td>
                      <td>{line.rangeStart}</td>
                      <td>{line.rangeEnd}</td>
                      <td className="text-end">{formatNumber(line.quantity)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Collapse>

        {/* Mục 3, README.md (Cập nhật) — đối chiếu Giao/Thu hồi/Bán và công nợ ước tính của CẢ đại
            lý trong cùng kế hoạch (không chỉ riêng phiếu đang xem), cùng khái niệm với
            RecallDetailModal.tsx để 2 chiều giao/thu hồi đối chiếu được với nhau. */}
        <h6 className="mt-4 mb-2">{st('doi_chieu_cong_no_dai_ly_title')}</h6>
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-0">
            <tbody>
              <tr>
                <td className="fw-semibold bg-light" width="40%">{st('tong_ve_da_giao_text')}</td>
                <td>{formatNumber(delivery.agentTotalDeliveredQuantity)}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('tong_ve_da_thu_hoi_text')}</td>
                <td>{formatNumber(delivery.agentTotalRecalledQuantity)}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('so_ve_da_ban_duoc_text')}</td>
                <td className="text-success fw-semibold">
                  {formatNumber(delivery.agentTotalSoldQuantity)}
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('cong_no_uoc_tinh_text')}</td>
                <td className="fw-semibold">{formatCurrency(delivery.agentDebtValue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {st('dong_button')}
        </Button>
        {onPrint && PageActionPermission.canDisplay(location.pathname, 'button-print-delivery') && (
          <Button
            color="primary"
            outline
            data-action-code="button-print-delivery"
            data-action-name="In / lưu PDF phiếu giao vé"
            onClick={onPrint}
          >
            <i className="ri-printer-line align-bottom me-1"></i>
            {st('in_phieu_pdf_button')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default DeliveryDetailModal;
